import { Card, CardContent, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { CHART_COLORS } from '@/_shared/lib/charts/colors';

interface FunnelStep {
  label: string;
  count: number;
}

interface FunnelChartProps {
  title: string;
  steps: FunnelStep[];
  loading?: boolean;
  /** Panel toolbar, rendered on the header row so it lines up with the title. */
  actions?: React.ReactNode;
}

function formatCount(count: number): string {
  return count.toLocaleString('en-US');
}

function getBarWidthPercent(count: number, maxCount: number): number {
  if (maxCount === 0) return 0;
  return (count / maxCount) * 100;
}

export function FunnelChart({ title, steps, loading, actions }: FunnelChartProps): React.ReactElement {
  // `min-w-0` on the header row keeps a long title inside the card — see MetricCard.
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex min-w-0 items-center justify-between gap-2">
            <CardTitle className="truncate text-sm font-medium text-muted-foreground" title={title}>
              {title}
            </CardTitle>
            {actions}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-8 w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const firstCount = steps[0]?.count ?? 0;
  // Scale bars against the largest finite step so non-monotonic/NaN data can't break every bar's width.
  const maxCount = steps.reduce((max, step) => Math.max(max, Number.isFinite(step.count) ? step.count : 0), 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex min-w-0 items-center justify-between gap-2">
          <CardTitle className="truncate text-sm font-medium text-muted-foreground" title={title}>
            {title}
          </CardTitle>
          {actions}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {steps.map((step, index) => {
            const color = CHART_COLORS[index % CHART_COLORS.length];
            const widthPercent = getBarWidthPercent(step.count, maxCount);
            const overallPercent = firstCount > 0 ? ((step.count / firstCount) * 100).toFixed(0) : '0';

            const prevStep = index > 0 ? steps[index - 1] : undefined;
            const conversionRate =
              prevStep && prevStep.count > 0 ? ((step.count / prevStep.count) * 100).toFixed(1) : undefined;
            // Flip the arrow when a step grows (bad data) rather than show ↓ on a >100% rate.
            const increasedFromPrev = prevStep !== undefined && step.count > prevStep.count;

            return (
              <div key={step.label}>
                {conversionRate !== undefined && (
                  <div className="flex items-center gap-2 py-1 pl-4">
                    <span className="text-xs text-muted-foreground">
                      {increasedFromPrev ? '\u2191' : '\u2193'} {conversionRate}%
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <span className="w-28 shrink-0 truncate text-xs text-muted-foreground">{step.label}</span>
                  <div className="relative h-6 flex-1 overflow-hidden rounded bg-card">
                    <div
                      className="h-full rounded"
                      style={{
                        width: `${widthPercent}%`,
                        backgroundColor: color,
                        minWidth: step.count > 0 ? '4px' : '0px',
                        transition: 'width 0.4s ease-out',
                      }}
                    />
                  </div>
                  <span className="w-20 shrink-0 text-right text-xs text-foreground tabular-nums">
                    {formatCount(step.count)}
                  </span>
                  <span className="w-12 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    {overallPercent}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
